import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  Briefcase, MapPin, Clock, PoundSterling, Building2, Calendar, 
  ArrowLeft, ExternalLink, Send, Upload, CheckCircle, Loader2,
  User, Award, FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { jobsAPI } from '../services/api';
import { toast } from 'sonner';
import { parseErrorMessage } from '../utils/errorHandler';

const JobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  
  // Application form state
  const [applicationForm, setApplicationForm] = useState({
    coverLetter: '',
    cv: null
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getJob(jobId);
      setJob(response.data);
      
      // Check if user has already applied
      try {
        const myApps = await jobsAPI.getMyApplications();
        const hasApplied = myApps.data.some(app => app.jobId === jobId);
        setApplied(hasApplied);
      } catch (err) {
        // Ignore if can't fetch applications
      }
    } catch (error) {
      toast.error(parseErrorMessage(error) || 'Failed to load job details');
      navigate('/jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!applicationForm.coverLetter.trim()) {
      toast.error('Please write a cover letter');
      return;
    }

    try {
      setApplying(true);
      await jobsAPI.applyToJob(jobId, {
        coverLetter: applicationForm.coverLetter,
        cv: applicationForm.cv
      });
      
      toast.success('Application submitted successfully!');
      setApplied(true);
      setShowApplyModal(false);
      setApplicationForm({ coverLetter: '', cv: null });
    } catch (error) {
      toast.error(parseErrorMessage(error) || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const handleCVChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('CV file must be less than 5MB');
        return;
      }
      setApplicationForm(prev => ({ ...prev, cv: file }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-200px)]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
          <Briefcase className="w-16 h-16 text-muted-foreground mb-4" />
          <p className="text-muted-foreground">Job not found</p>
          <Button onClick={() => navigate('/jobs')} className="mt-4">
            Back to Jobs
          </Button>
        </div>
      </div>
    );
  }

  const isOwnJob = job.postedBy?._id === currentUser._id || job.postedBy?.id === currentUser._id;
  const canApply = !isOwnJob && job.status === 'open' && !applied;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 py-6 max-w-4xl pb-24 md:pb-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/jobs')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Jobs
        </Button>

        {/* Job Header Card */}
        <Card className="mb-6">
          {job.imageUrl && (
            <div className="w-full h-48 md:h-64 overflow-hidden rounded-t-lg">
              <img 
                src={job.imageUrl} 
                alt={job.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Badge 
                    variant={job.status === 'open' ? 'default' : 'secondary'}
                    className={job.status === 'open' ? 'bg-primary' : 'bg-muted'}
                  >
                    {job.status === 'open' ? 'Open' : 'Closed'}
                  </Badge>
                  <Badge variant="outline">{job.employmentType}</Badge>
                </div>
                <CardTitle className="text-2xl md:text-3xl mb-2">{job.title}</CardTitle>
                
                {/* Posted By */}
                {job.postedBy && (
                  <div 
                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 mb-3"
                    onClick={() => navigate(`/profile/${job.postedBy.username}`)}
                  >
                    <img 
                      src={job.postedBy.profileImage || '/default-avatar.png'} 
                      alt={job.postedBy.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-medium text-sm">{job.postedBy.name}</p>
                      <p className="text-xs text-muted-foreground capitalize">{job.postedBy.persona}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Apply Button */}
              <div className="flex flex-col gap-2">
                {canApply && (
                  <Dialog open={showApplyModal} onOpenChange={setShowApplyModal}>
                    <DialogTrigger asChild>
                      <Button size="lg" className="w-full md:w-auto">
                        <Send className="w-4 h-4 mr-2" />
                        Apply Now
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <DialogTitle>Apply for {job.title}</DialogTitle>
                        <DialogDescription>
                          Submit your application to {job.postedBy?.name || 'the employer'}
                        </DialogDescription>
                      </DialogHeader>
                      
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="coverLetter">Cover Letter *</Label>
                          <Textarea
                            id="coverLetter"
                            placeholder="Introduce yourself and explain why you're a great fit for this role..."
                            value={applicationForm.coverLetter}
                            onChange={(e) => setApplicationForm(prev => ({ 
                              ...prev, 
                              coverLetter: e.target.value 
                            }))}
                            rows={6}
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="cv">Upload CV (Optional)</Label>
                          <div className="mt-1">
                            <Input
                              id="cv"
                              type="file"
                              accept=".pdf,.doc,.docx"
                              onChange={handleCVChange}
                              className="cursor-pointer"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              PDF, DOC, DOCX (Max 5MB)
                            </p>
                          </div>
                          {applicationForm.cv && (
                            <p className="text-sm text-primary mt-2 flex items-center">
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {applicationForm.cv.name}
                            </p>
                          )}
                        </div>

                        <div className="flex gap-2 pt-4">
                          <Button 
                            variant="outline" 
                            onClick={() => setShowApplyModal(false)}
                            className="flex-1"
                          >
                            Cancel
                          </Button>
                          <Button 
                            onClick={handleApply}
                            disabled={applying || !applicationForm.coverLetter.trim()}
                            className="flex-1"
                          >
                            {applying ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 mr-2" />
                                Submit Application
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {applied && (
                  <div className="flex items-center gap-2 text-primary bg-primary/10 px-4 py-2 rounded-lg">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Applied</span>
                  </div>
                )}

                {job.status === 'closed' && !applied && (
                  <div className="text-muted-foreground text-sm">
                    This position is no longer accepting applications
                  </div>
                )}

                {job.applicationUrl && (
                  <Button variant="outline" asChild>
                    <a href={job.applicationUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Apply Externally
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {/* Job Meta Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {job.location && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">{job.location}</span>
                </div>
              )}
              {job.salary && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <PoundSterling className="w-4 h-4" />
                  <span className="text-sm">{job.salary}</span>
                </div>
              )}
              {job.jobType && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Briefcase className="w-4 h-4" />
                  <span className="text-sm">{job.jobType}</span>
                </div>
              )}
              {job.createdAt && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">Posted {formatDate(job.createdAt)}</span>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {job.sport && (
                <Badge variant="secondary">{job.sport}</Badge>
              )}
              {job.experienceLevel && (
                <Badge variant="outline">{job.experienceLevel}</Badge>
              )}
              {job.applicationsCount !== undefined && (
                <Badge variant="outline">
                  <User className="w-3 h-3 mr-1" />
                  {job.applicationsCount} applicant{job.applicationsCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Job Description */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Job Description
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
                {job.description}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Requirements */}
        {job.requirements && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="w-5 h-5" />
                Requirements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap text-muted-foreground leading-relaxed">
                  {job.requirements}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Bottom Apply CTA */}
        {canApply && (
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg">Interested in this role?</h3>
                  <p className="text-muted-foreground text-sm">
                    Submit your application and take the next step in your career
                  </p>
                </div>
                <Button size="lg" onClick={() => setShowApplyModal(true)}>
                  <Send className="w-4 h-4 mr-2" />
                  Apply Now
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

    </div>
  );
};

export default JobDetail;
